import { useState, useEffect } from 'react';
import { useSocieta } from '../context/SocietaContext';
import { usePermesso } from '../context/PermessiContext';

/**
 * Hook per le pagine che devono filtrare per società.
 *
 * Comportamento:
 * - Topbar ha società selezionata (societaAttiva !== null):
 *   societaId = societaAttiva.id, isDisabled = true, setSocietaId è no-op
 * - Topbar = "Tutte le società" (societaAttiva === null):
 *   societaId = stato locale della pagina, isDisabled = false
 *   setSocietaId aggiorna solo lo stato locale, MAI il contesto topbar
 */
export function useFiltroSocieta() {
  const { societaAttiva, listaSocieta, isSocietaFissa, loading } = useSocieta();
  const { isSuperadmin } = usePermesso();

  // Stato locale — attivo solo quando topbar = "Tutte le società"
  const [localId, setLocalId] = useState('');

  // Reset locale quando topbar torna a null (cambio da specifica → tutte)
  useEffect(() => {
    if (!societaAttiva) setLocalId('');
  }, [societaAttiva]);

  const isTopbarFixed = !!societaAttiva;
  // isDisabled = topbar ha selezionato qualcosa, OPPURE utente ha 1 sola società
  const isDisabled = isTopbarFixed || isSocietaFissa;

  // Determina societaId:
  // 1. Se topbar ha una società, usa quella
  // 2. Se società fissa (utente single-società) e societaAttiva esiste, usa quella
  // 3. Altrimenti usa lo stato locale
  let societaId = '';
  if (isTopbarFixed && societaAttiva) {
    societaId = String(societaAttiva.id);
  } else if (isSocietaFissa && listaSocieta.length === 1) {
    societaId = String(listaSocieta[0].id);
  } else {
    societaId = localId;
  }

  const setSocietaId = (id: string) => {
    // Non aggiorna MAI il contesto topbar
    if (!isDisabled) setLocalId(id);
  };

  return {
    /** societaId selezionata (stringa vuota = non selezionata) */
    societaId,
    setSocietaId,
    /** Lista società visibili all'utente */
    listaSocieta,
    loading,
    /**
     * true quando la combo società va nascosta/disabilitata:
     * - utente ha 1 sola società, OPPURE
     * - topbar ha una società selezionata (non modificabile dalla pagina)
     */
    isUnica: isDisabled,
    /** Alias esplicito per il controllo disabled */
    isDisabled,
    /**
     * true quando i dati possono essere caricati.
     * - SUPERADMIN: sempre true (vede tutto senza filtro)
     * - UTENTE: true solo quando c'è una società selezionata
     */
    pronta: !!societaId || isSuperadmin,
  };
}
